// AJAX submission for the quote and contact forms.
//
// Posts a single "data" field: a JSON array of {name, value, label} for every field, which
// FormMailer (app/classes/formmailer.php) turns into the email. The label is the text of the
// field's <label for="...">. A reCAPTCHA Enterprise token for the action "quote" or "contact" goes
// alongside it as "recaptchaToken" (checked by app/classes/recaptcha.php).

const NETWORK_ERROR = "Sorry, we couldn't send your request. Please check your connection and try again.";
const RECAPTCHA_TIMEOUT = 10000;

/**
 * @param {HTMLFormElement|null} form  #quote-form or #contact-form
 * @param {{includePostOrigin?: boolean}} options  add the ?postOrigin= blog post the visitor came from
 */
export function initForm(form, { includePostOrigin = false } = {}) {
  if (!form) return;
  const prefix = form.id.replace(/-form$/, ""); // "quote" or "contact"
  const message = document.getElementById(`${prefix}-message`);
  const validation = document.getElementById(`${prefix}-validation`);
  const submit = form.querySelector("[type=submit]");
  const siteKey = form.querySelector("[data-recaptcha-site-key]")?.dataset.recaptchaSiteKey;

  // reCAPTCHA's scripts are large, so they only load once the visitor starts filling in the form
  form.addEventListener("focusin", () => loadRecaptcha(siteKey).catch(() => {}), { once: true });

  form.addEventListener("submit", async (event) => {
    event.preventDefault();

    const fields = formFields(form);
    if (includePostOrigin) {
      const origin = new URLSearchParams(window.location.search).get("postOrigin");
      fields.push({ name: "postOrigin", value: origin ? origin.replaceAll(/-|_/g, " ") : "--", label: "Blog Post Origin" });
    }

    submit.disabled = true;
    try {
      const recaptchaToken = await getRecaptchaToken(siteKey, prefix);
      const response = await fetch(form.action, {
        method: "POST",
        body: new URLSearchParams({ data: JSON.stringify(fields), recaptchaToken }),
      });
      const text = await response.text();
      if (response.ok) {
        await fade(form, 1, 0, 400);
        form.hidden = true;
        message.querySelector(".message").innerHTML = text;
        fade(message, 0, 1, 1000);
      } else {
        showError(validation, text);
      }
    } catch {
      showError(validation, NETWORK_ERROR);
    } finally {
      submit.disabled = false;
    }
  });
}

let recaptcha;

/** Loads reCAPTCHA Enterprise once per page; resolves to grecaptcha.enterprise when it's ready. */
function loadRecaptcha(siteKey) {
  recaptcha ??= new Promise((resolve, reject) => {
    if (!siteKey) return reject(new Error("No reCAPTCHA site key"));
    // Added from here rather than as a <script> tag so the page needs no inline script for it
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.onload = () => grecaptcha.enterprise.ready(() => resolve(grecaptcha.enterprise));
    script.onerror = () => {
      script.remove();
      recaptcha = null; // let the next submit try again
      reject(new Error("reCAPTCHA failed to load"));
    };
    document.head.appendChild(script);
  });
  return recaptcha;
}

/** A fresh token (they expire after two minutes), or "" if reCAPTCHA is blocked or unavailable. */
async function getRecaptchaToken(siteKey, action) {
  try {
    const enterprise = await withTimeout(loadRecaptcha(siteKey), RECAPTCHA_TIMEOUT);
    return await withTimeout(enterprise.execute(siteKey, { action }), RECAPTCHA_TIMEOUT);
  } catch {
    // The server turns the submission away with a message saying to email instead
    return "";
  }
}

function withTimeout(promise, ms) {
  return Promise.race([promise, new Promise((_, reject) => setTimeout(() => reject(new Error("Timed out")), ms))]);
}

function formFields(form) {
  return [...new FormData(form)].map(([name, value]) => ({
    name,
    value,
    label: [...document.querySelectorAll(`label[for="${CSS.escape(name)}"]`)].map((l) => l.textContent).join(""),
  }));
}

function showError(element, html) {
  element.innerHTML = html;
  element.style.display = "block";
}

function fade(element, from, to, duration) {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return Promise.resolve();
  return element.animate({ opacity: [from, to] }, { duration, easing: "ease" }).finished;
}
